/**
 * Which voice starts speaking first, and by how much (task 6).
 *
 * The question the second voice raised on its first real call: it *felt*
 * faster than the translation provider's own voice. "Voice-over delay" cannot
 * answer that — it measures ElevenLabs against its own input, the finished
 * phrase, and says nothing about when the built-in voice would have spoken.
 *
 * So on a seat that hears ElevenLabs, both voices are timed against the same
 * event and nothing else: the first translated word of an utterance arriving
 * from the translation provider. The built-in voice still arrives on that seat
 * — it is kept, unplayed, as the way back if ElevenLabs fails — so it can be
 * listened to without anyone hearing it.
 *
 *   - **Utterance.** A run of translated text with no gap longer than the pause
 *     that ends a phrase. Its start, T0, is the first fragment of the run.
 *   - **Built-in voice.** The first speech onset in the provider's audio after
 *     T0. By loudness, because `playout.ts` found the provider sends audio
 *     continuously, silence included: "audio arrived" means nothing.
 *   - **ElevenLabs.** The moment its first audible sample for that utterance
 *     is scheduled to play — the playback clock, not the arrival of the bytes,
 *     and past any leading silence in the chunk, so the two sides are judged
 *     by the same rule: when a listener would first hear a voice.
 *
 * `lead = builtIn − elevenLabs`: positive means ElevenLabs was heard first.
 *
 * What this cannot see, and says so rather than guessing: an utterance whose
 * built-in voice runs straight on from the one before has no onset of its own,
 * and is left out. Output-device latency is the same for both and is not in
 * either figure. Everything below is pure except `listenForOnsets`, so the
 * pairing is tested without a sound card.
 */

/**
 * The gap that separates two utterances. Kept at the 700 ms the phrase pause
 * had when these numbers were first measured, rather than following the
 * shorter pause, so that calls before and after that change are still timed
 * against the same idea of an utterance and can be compared.
 */
const UTTERANCE_GAP_MS = 700;

/**
 * RMS above which a frame counts as voice. The same -34 dBFS as the wake
 * detector in `idle.ts`: translated speech is well above it, the provider's
 * silence well below.
 */
export const ONSET_LEVEL = 0.02;
/** Frame spacing for the built-in voice's detector. */
export const FRAME_MS = 50;
/** Loud frames in a row that make an onset: 150 ms, shorter than any word. */
const ONSET_FRAMES = 3;
/** Quiet frames in a row that end speech and re-arm the detector: 400 ms. */
const QUIET_FRAMES = 8;
/**
 * The longest the built-in voice may take to start an utterance and still be
 * paired with it. Past this, an onset belongs to something later and the
 * older utterance is let go instead of being matched to the wrong speech.
 */
const MAX_WAIT_MS = 8_000;

/**
 * Edges, not levels: one onset per stretch of speech. Stateful, but fed plain
 * numbers, so it is tested with arrays.
 */
export class OnsetDetector {
  readonly #level: number;
  readonly #frames: number;
  readonly #quiet: number;
  readonly #frameMs: number;
  #speaking = false;
  #loudRun = 0;
  #quietRun = 0;

  constructor(
    level = ONSET_LEVEL,
    frames = ONSET_FRAMES,
    quiet = QUIET_FRAMES,
    frameMs = FRAME_MS,
  ) {
    this.#level = level;
    this.#frames = frames;
    this.#quiet = quiet;
    this.#frameMs = frameMs;
  }

  /**
   * One frame's RMS, measured at `at`. Returns when the speech began — the
   * first loud frame of the run, not the frame that confirmed it — or null.
   */
  push(rms: number, at: number): number | null {
    const loud = rms >= this.#level;
    if (this.#speaking) {
      this.#quietRun = loud ? 0 : this.#quietRun + 1;
      if (this.#quietRun >= this.#quiet) {
        this.#speaking = false;
        this.#loudRun = 0;
      }
      return null;
    }
    this.#loudRun = loud ? this.#loudRun + 1 : 0;
    if (this.#loudRun < this.#frames) return null;
    this.#speaking = true;
    this.#quietRun = 0;
    return at - (this.#frames - 1) * this.#frameMs;
  }
}

/**
 * Seconds into `samples` at which sound first reaches `level`, judged over
 * 10 ms windows, or null if it never does. For ElevenLabs' chunks, which
 * often open with a breath of silence.
 */
export function firstLoudOffset(
  samples: Float32Array,
  sampleRate: number,
  level = ONSET_LEVEL,
): number | null {
  const win = Math.max(1, Math.round(sampleRate / 100));
  for (let from = 0; from < samples.length; from += win) {
    const to = Math.min(samples.length, from + win);
    let sum = 0;
    for (let i = from; i < to; i += 1) sum += samples[i]! * samples[i]!;
    if (Math.sqrt(sum / (to - from)) >= level) return from / sampleRate;
  }
  return null;
}

export interface LeadStats {
  /** The latest utterance's lead, ms. Positive: ElevenLabs was heard first. */
  lastMs: number | null;
  /** Mean over every paired utterance this call. */
  avgMs: number | null;
  count: number;
}

export const NO_LEAD: LeadStats = { lastMs: null, avgMs: null, count: 0 };

interface Utterance {
  t0: number;
  last: number;
  builtInAt: number | null;
  elevenAt: number | null;
}

/** Pairs the two voices' starts utterance by utterance. All times in ms, one clock. */
export class VoiceRace {
  readonly #gapMs: number;
  #open: Utterance[] = [];
  #sum = 0;
  #count = 0;
  #last: number | null = null;

  constructor(gapMs = UTTERANCE_GAP_MS) {
    this.#gapMs = gapMs;
  }

  get stats(): LeadStats {
    return {
      lastMs: this.#last,
      avgMs: this.#count === 0 ? null : Math.round(this.#sum / this.#count),
      count: this.#count,
    };
  }

  /** A fragment of translated text arrived. */
  text(at: number): void {
    const current = this.#open.at(-1);
    if (current && at - current.last <= this.#gapMs) {
      current.last = at;
      return;
    }
    this.#open.push({ t0: at, last: at, builtInAt: null, elevenAt: null });
  }

  /**
   * The built-in voice started speaking at `at`. It speaks utterances in
   * order, so the onset goes to the oldest one still waiting for it — after
   * letting go of any that waited too long to have been this one.
   */
  builtIn(at: number): LeadStats | null {
    this.#open = this.#open.filter((u) => u.builtInAt !== null || at - u.t0 <= MAX_WAIT_MS);
    const u = this.#open.find((x) => x.builtInAt === null && x.t0 <= at);
    if (!u) return null;
    u.builtInAt = at;
    return this.#settle(u);
  }

  /**
   * ElevenLabs started speaking at `at` a phrase whose text began arriving at
   * `textAt`. Only an utterance's first phrase counts: later phrases of the
   * same run are not a start.
   */
  eleven(textAt: number, at: number): LeadStats | null {
    const u = this.#open.findLast((x) => x.t0 <= textAt);
    if (!u || u.elevenAt !== null) return null;
    u.elevenAt = at;
    return this.#settle(u);
  }

  #settle(u: Utterance): LeadStats | null {
    if (u.builtInAt === null || u.elevenAt === null) return null;
    const lead = Math.round(u.builtInAt - u.elevenAt);
    this.#last = lead;
    this.#sum += lead;
    this.#count += 1;
    this.#open = this.#open.filter((x) => x !== u);
    return this.stats;
  }
}

/**
 * Calls `onOnset` with the start of every stretch of speech in `stream`,
 * without playing it. Returns the way to stop. Never throws: a browser that
 * cannot do this simply measures nothing.
 *
 * The stream is also attached to a muted media element. Chrome delivers a
 * remote WebRTC track to Web Audio only while something is playing it, and
 * this track is deliberately not played anywhere else on this seat.
 */
export function listenForOnsets(stream: MediaStream, onOnset: (at: number) => void): () => void {
  let ctx: AudioContext;
  try {
    ctx = new AudioContext();
  } catch {
    return () => undefined;
  }
  const keeper = new Audio();
  keeper.muted = true;
  keeper.srcObject = stream;
  void keeper.play().catch(() => undefined);

  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  source.connect(analyser);
  void ctx.resume().catch(() => undefined);

  const detector = new OnsetDetector();
  const frame = new Float32Array(analyser.fftSize);
  const timer = setInterval(() => {
    analyser.getFloatTimeDomainData(frame);
    let sum = 0;
    for (const s of frame) sum += s * s;
    const onset = detector.push(Math.sqrt(sum / frame.length), performance.now());
    if (onset !== null) onOnset(onset);
  }, FRAME_MS);

  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    clearInterval(timer);
    source.disconnect();
    keeper.srcObject = null;
    void ctx.close().catch(() => undefined);
  };
}
